import { InjectionToken, Optional, Self } from '@angular/core';
import { NgControl } from '@angular/forms';
import { tuiControlValueFactory } from '@taiga-ui/kit/utils';
import { tuiExtractI18n } from '@taiga-ui/i18n';
import { TUI_FIRST_DAY, TUI_LAST_DAY, TUI_DEFAULT_STRINGIFY, TUI_DEFAULT_IDENTITY_MATCHER, ALWAYS_FALSE_HANDLER } from '@taiga-ui/cdk';

const TUI_CALENDAR_DATE_STREAM = new InjectionToken(`[TUI_CALENDAR_DATE_STREAM]: Stream that emits calendar data change`);
function tuiDateStreamWithTransformer(transformer) {
    return {
        provide: TUI_CALENDAR_DATE_STREAM,
        deps: [
            [new Optional(), new Self(), NgControl],
            [new Optional(), transformer],
        ],
        useFactory: tuiControlValueFactory,
    };
}

const TUI_DATE_VALUE_TRANSFORMER = new InjectionToken(`[TUI_DATE_VALUE_TRANSFORMER]: Control value transformer of TuiDay to custom value format for InputDate* components`);
const TUI_DATE_RANGE_VALUE_TRANSFORMER = new InjectionToken(`[TUI_DATE_RANGE_VALUE_TRANSFORMER]: Control value transformer for InputDateRange component`);
const TUI_DATE_TIME_VALUE_TRANSFORMER = new InjectionToken(`[TUI_DATE_TIME_VALUE_TRANSFORMER]: Control value transformer for InputDateTime component`);

const TUI_PROMPT_WORDS = new InjectionToken(`[TUI_PROMPT_WORDS]: i18n 'prompt' words`, {
    factory: tuiExtractI18n(`prompt`),
});
const TUI_CANCEL_WORD = new InjectionToken(`[TUI_CANCEL_WORD]: i18n 'cancel' word`, {
    factory: tuiExtractI18n(`cancel`),
});
const TUI_DONE_WORD = new InjectionToken(`[TUI_DONE_WORD]: i18n 'done' word`, {
    factory: tuiExtractI18n(`done`),
});
const TUI_MORE_WORD = new InjectionToken(`[TUI_MORE_WORD]: i18n 'more' word`, {
    factory: tuiExtractI18n(`more`),
});
const TUI_HIDE_TEXT = new InjectionToken(`[TUI_HIDE_TEXT]: i18n 'hide' word`, {
    factory: tuiExtractI18n(`hide`),
});
const TUI_SHOW_ALL_TEXT = new InjectionToken(`[TUI_SHOW_ALL_TEXT]: i18n 'show all' word`, {
    factory: tuiExtractI18n(`showAll`),
});
const TUI_OTHER_DATE_TEXT = new InjectionToken(`[TUI_OTHER_DATE_TEXT]: i18n 'Other date' text`, {
    factory: tuiExtractI18n(`otherDate`),
});
const TUI_CHOOSE_DAY_OR_RANGE_TEXTS = new InjectionToken(`[TUI_CHOOSE_DAY_OR_RANGE_TEXTS]: choose day or range i18n texts`, {
    factory: tuiExtractI18n(`mobileCalendarTexts`),
});
const TUI_FROM_TO_TEXTS = new InjectionToken(`[TUI_FROM_TO_TEXTS]: from and to i18n texts`, {
    factory: tuiExtractI18n(`range`),
});
const TUI_PLUS_MINUS_TEXTS = new InjectionToken(`[TUI_PLUS_MINUS_TEXTS]: plus and minus i18n texts`, {
    factory: tuiExtractI18n(`countTexts`),
});
const TUI_TIME_TEXTS = new InjectionToken(`[TUI_TIME_TEXTS]: time i18n texts`, {
    factory: tuiExtractI18n(`time`),
});
const TUI_DATE_TEXTS = new InjectionToken(`[TUI_DATE_TEXTS]: date format i18n texts`, {
    factory: tuiExtractI18n(`dateTexts`),
});
const TUI_DIGITAL_INFORMATION_UNITS = new InjectionToken(`[TUI_DIGITAL_INFORMATION_UNITS]: short bytes, kilobytes and megabytes i18n texts`, {
    factory: tuiExtractI18n(`digitalInformationUnits`),
});
const TUI_COPY_TEXTS = new InjectionToken(`[TUI_COPY_TEXTS]: copy i18n texts`, {
    factory: tuiExtractI18n(`copyTexts`),
});
const TUI_PASSWORD_TEXTS = new InjectionToken(`[TUI_PASSWORD_TEXTS]: password i18n texts`, {
    factory: tuiExtractI18n(`passwordTexts`),
});
const TUI_CALENDAR_MONTHS = new InjectionToken(`[TUI_CALENDAR_MONTHS]: short calendar months i18n`, {
    factory: tuiExtractI18n(`shortCalendarMonths`),
});
const TUI_FILE_TEXTS = new InjectionToken(`[TUI_FILE_TEXTS]: file i18n texts`, {
    factory: tuiExtractI18n(`fileTexts`),
});
const TUI_PAGINATION_TEXTS = new InjectionToken(`[TUI_PAGINATION_TEXTS]: pagination i18n texts`, {
    factory: tuiExtractI18n(`pagination`),
});
const TUI_INPUT_FILE_TEXTS = new InjectionToken(`[TUI_INPUT_FILE_TEXTS]: tui-input-file i18n texts`, {
    factory: tuiExtractI18n(`inputFileTexts`),
});
const TUI_MULTI_SELECT_TEXTS = new InjectionToken(`[TUI_MULTI_SELECT_TEXTS]: tui-multi-select i18n texts`, {
    factory: tuiExtractI18n(`multiSelectTexts`),
});
const TUI_COUNTRIES = new InjectionToken(`[TUI_COUNTRIES]: Localized countries names`, {
    factory: tuiExtractI18n(`countries`),
});

const TUI_INPUT_DATE_DEFAULT_OPTIONS = {
    icon: ({ $implicit }) => $implicit === `s` ? `tuiIconCalendar` : `tuiIconCalendarLarge`,
    min: TUI_FIRST_DAY,
    max: TUI_LAST_DAY,
};
const TUI_INPUT_DATE_OPTIONS = new InjectionToken(`[TUI_INPUT_DATE_OPTIONS]: Default parameters for date input component`, {
    factory: () => TUI_INPUT_DATE_DEFAULT_OPTIONS,
});
const tuiInputDateOptionsProvider = (options) => ({
    provide: TUI_INPUT_DATE_OPTIONS,
    useValue: Object.assign(Object.assign({}, TUI_INPUT_DATE_DEFAULT_OPTIONS), options),
});

const TUI_DEFAULT_ITEMS_HANDLERS = {
    stringify: TUI_DEFAULT_STRINGIFY,
    identityMatcher: TUI_DEFAULT_IDENTITY_MATCHER,
    disabledItemHandler: ALWAYS_FALSE_HANDLER,
};
const TUI_ITEMS_HANDLERS = new InjectionToken(`[TUI_ITEMS_HANDLERS]: Default items handlers for components`, {
    factory: () => TUI_DEFAULT_ITEMS_HANDLERS,
});
const tuiItemsHandlersProvider = (options) => ({
    provide: TUI_ITEMS_HANDLERS,
    useValue: Object.assign(Object.assign({}, TUI_DEFAULT_ITEMS_HANDLERS), options),
});

const TUI_MOBILE_CALENDAR = new InjectionToken(`[TUI_MOBILE_CALENDAR]: A component for mobile data picker`);

const TUI_MONTH_FORMATTER = new InjectionToken(`[TUI_MONTH_FORMATTER]: A function to get localized formatted month`);

const TUI_TAB_MARGIN = new InjectionToken(`[TUI_TAB_MARGIN]: Margin between tabs`, {
    factory: () => 24,
});

const TUI_VALIDATION_ERRORS = new InjectionToken(`[TUI_VALIDATION_ERRORS]: Validation errors`, {
    factory: () => ({}),
});

/**
 * Generated bundle index. Do not edit.
 */

export { TUI_CALENDAR_DATE_STREAM, TUI_CALENDAR_MONTHS, TUI_CANCEL_WORD, TUI_CHOOSE_DAY_OR_RANGE_TEXTS, TUI_COPY_TEXTS, TUI_COUNTRIES, TUI_DATE_RANGE_VALUE_TRANSFORMER, TUI_DATE_TEXTS, TUI_DATE_TIME_VALUE_TRANSFORMER, TUI_DATE_VALUE_TRANSFORMER, TUI_DEFAULT_ITEMS_HANDLERS, TUI_DIGITAL_INFORMATION_UNITS, TUI_DONE_WORD, TUI_FILE_TEXTS, TUI_FROM_TO_TEXTS, TUI_HIDE_TEXT, TUI_INPUT_DATE_DEFAULT_OPTIONS, TUI_INPUT_DATE_OPTIONS, TUI_INPUT_FILE_TEXTS, TUI_ITEMS_HANDLERS, TUI_MOBILE_CALENDAR, TUI_MONTH_FORMATTER, TUI_MORE_WORD, TUI_MULTI_SELECT_TEXTS, TUI_OTHER_DATE_TEXT, TUI_PAGINATION_TEXTS, TUI_PASSWORD_TEXTS, TUI_PLUS_MINUS_TEXTS, TUI_PROMPT_WORDS, TUI_SHOW_ALL_TEXT, TUI_TAB_MARGIN, TUI_TIME_TEXTS, TUI_VALIDATION_ERRORS, tuiDateStreamWithTransformer, tuiInputDateOptionsProvider, tuiItemsHandlersProvider };
